import { useEffect, useRef, useState } from "react";
import { Icon } from "../icons/Icon.js";
import { isLanguageCode, SUPPORTED_LANGUAGES } from "../i18n/languages.js";
import { useLanguage, useT } from "../i18n/useT.js";

/**
 * Compact language trigger: shows the current language's two-letter code,
 * expands to a listbox of every supported language's own native name (never
 * translated — a speaker looking for their language must find it regardless
 * of the UI's current language).
 */
export function LanguageSwitcher({ className = "" }: { className?: string }) {
  const { language, setLanguage } = useLanguage();
  const t = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function choose(code: string) {
    setOpen(false);
    if (isLanguageCode(code) && code !== language) void setLanguage(code);
  }

  return (
    <div className={`mmm-language-switcher ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className="mmm-language-switcher-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("settings.language")}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">{language.toUpperCase()}</span>
        <Icon name="chevron" className={`mmm-language-switcher-chevron ${open ? "is-open" : ""}`.trim()} />
      </button>
      {open && (
        <ul className="mmm-language-switcher-menu" role="listbox" aria-label={t("settings.language")}>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <li key={lang.code} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={lang.code === language}
                className={`mmm-language-switcher-option ${lang.code === language ? "is-selected" : ""}`.trim()}
                onClick={() => choose(lang.code)}
              >
                <span className="mmm-language-switcher-option-code" aria-hidden="true">
                  {lang.code.toUpperCase()}
                </span>
                <span>{lang.nativeName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
